/**
 * LoginScreen — shown to unauthenticated users.
 *
 * Two paths:
 *  1. "Sign in (dev)" — calls POST /auth/dev-login locally, no OAuth creds needed.
 *  2. "Sign in with GitHub" — points at GET /auth/github (real OAuth flow).
 */

import React, { useState } from "react";
import { useAuth } from "../providers/AINativeProvider.js";

export function LoginScreen() {
  const { devLogin, loading, error } = useAuth();
  const [devSub, setDevSub] = useState("dev:local");
  const [devName, setDevName] = useState("Dev User");
  const [showDevForm, setShowDevForm] = useState(false);

  const apiUrl =
    (import.meta.env["VITE_API_URL"] as string | undefined) ??
    "http://localhost:3001";

  async function handleDevLogin(e: React.FormEvent) {
    e.preventDefault();
    await devLogin({ sub: devSub, name: devName });
  }

  return (
    <div className="login-screen" role="main" aria-label="BuildOS sign in">
      <div className="login-screen__card">
        {/* Logo / wordmark */}
        <div className="login-screen__logo" aria-hidden="true">
          <span className="login-screen__logo-mark">B</span>
        </div>

        <h1 className="login-screen__title">BuildOS</h1>
        <p className="login-screen__subtitle">
          AI-Native Operating System for Real Estate Developers
        </p>

        <div className="login-screen__actions">
          {/* GitHub OAuth — primary path in production */}
          <a
            href={`${apiUrl}/auth/github`}
            className="login-screen__btn login-screen__btn--github"
            aria-label="Sign in with GitHub"
          >
            <svg
              className="login-screen__github-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              width="20"
              height="20"
            >
              <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
            </svg>
            Sign in with GitHub
          </a>

          {/* Dev login — always available locally */}
          <button
            type="button"
            className="login-screen__btn login-screen__btn--dev"
            onClick={() => setShowDevForm((v) => !v)}
            aria-expanded={showDevForm}
          >
            Sign in (dev)
          </button>
        </div>

        {/* Dev login form (collapsible) */}
        {showDevForm && (
          <form
            className="login-screen__dev-form"
            onSubmit={(e) => { void handleDevLogin(e); }}
            aria-label="Developer login form"
          >
            <label className="login-screen__dev-label" htmlFor="dev-sub">
              User ID (sub)
            </label>
            <input
              id="dev-sub"
              className="login-screen__dev-input"
              type="text"
              value={devSub}
              onChange={(e) => setDevSub(e.target.value)}
              required
              aria-required="true"
              placeholder="dev:local"
            />

            <label className="login-screen__dev-label" htmlFor="dev-name">
              Display name
            </label>
            <input
              id="dev-name"
              className="login-screen__dev-input"
              type="text"
              value={devName}
              onChange={(e) => setDevName(e.target.value)}
              placeholder="Dev User"
            />

            <button
              type="submit"
              className="login-screen__btn login-screen__btn--submit"
              disabled={loading || !devSub.trim()}
              aria-busy={loading}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        {/* Error display */}
        {error && (
          <div className="login-screen__error" role="alert" aria-live="polite">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
